import { useState, useEffect, useMemo, ReactNode } from 'react'
import { Bell, Sun } from 'react-feather'
import { useHistory } from 'react-router-dom'
import { CSSTransition } from 'react-transition-group'
import clsx from 'clsx'
import { useModal } from '@ebay/nice-modal-react'
import {
  pluralize,
  pluralizeWithoutCount,
} from '@pepper/utils/src/function/pluralize'

import { AlertBar, useProjectDraftList, useAuthState } from '@pepper/ui'
import useOrganisations from 'utils/hooks/useOrganisations'
import { useDashboardMetrics } from '../data'
import { dataLayerProjectCreationInitiateEvent } from '@pepper/utils/src/function/dataLayerEvents'
import { getRedirectionLink } from 'utils/functions/getRedirectionLink'
import StagePill from 'components/StagePill/StagePill'

import s from './AlertBarWrapper.module.css'

type Props = {
  companyId: string
  hasNoProjects: boolean
}

type Bar = {
  key: string
  id: string
  title: string | ReactNode
  subTitle: string
  ctaText: string
  value: number | null
  onCtaClick: () => void
  icon: React.ElementType
}
const DELAY = 2500

const AlertBarWrapper = ({
  companyId,
  hasNoProjects,
}: Props): JSX.Element | null => {
  const serviceSelectionDrawer = useModal('service-selection-drawer')
  const [ShowAlertBars, setShowAlertBars] = useState(false)
  const [barsLocalStorage, setBarsLocalStorage] = useState<string[] | null>(
    null
  )
  const { user } = useAuthState()
  const { selectedCompany } = useOrganisations(user)
  const history = useHistory()

  const { data: dashboardMetrics } = useDashboardMetrics(companyId)

  const { totalRecords: totalDraft, isLoading: isDraftsLoading } =
    useProjectDraftList('Project Draft', { company_id: companyId }, 1, 0)

  useEffect(() => {
    const handleDelay = setTimeout(() => {
      if (dashboardMetrics && !isDraftsLoading) {
        setShowAlertBars(true)
      }
    }, DELAY)
    return () => clearTimeout(handleDelay)
  }, [dashboardMetrics, isDraftsLoading])

  const pendingApprovalValue =
    dashboardMetrics?.total_assignments_pending_approval.value ?? 0

  const AlertBarArray: Bar[] = useMemo(() => {
    const draftItemsText = totalDraft > 1 ? 'items' : 'item'

    let barsArray

    if (selectedCompany) {
      barsArray = [
        {
          key: 'approval_pending',
          id: `${selectedCompany?.id}_approval_pending`,
          title: `You have ${pluralize(
            pendingApprovalValue,
            'order'
          )} submitted by the creators`,
          subTitle: `Please review the ${pluralizeWithoutCount(
            pendingApprovalValue,
            'order'
          )} that are in approval pending state.`,
          ctaText: `Review ${pluralize(pendingApprovalValue, 'order')}`,
          value: pendingApprovalValue,
          onCtaClick: () => getRedirectionLink('approval_pending', history),
          icon: Bell,
        },
        {
          key: 'pending_draft',
          id: `${selectedCompany?.id}_pending_draft`,
          title: `You have ${totalDraft} draft ${draftItemsText} pending`,
          subTitle: `Please review the projects which have draft items.`,
          ctaText: `Review ${totalDraft} ${draftItemsText}`,
          value: totalDraft,
          onCtaClick: () => history.push('/projects?projectList=drafts'),
          icon: Bell,
        },
      ]

      if (hasNoProjects) {
        barsArray.push({
          key: 'create_new_project',
          id: `${selectedCompany?.id}_create_new_project`,
          title: `Get started by creating a Project`,
          subTitle:
            'Projects are a collection of content items. You can create as many items as you want within a project',
          ctaText: `Create a Project`,
          value: 1,
          onCtaClick: () => {
            serviceSelectionDrawer.show()
            dataLayerProjectCreationInitiateEvent()
          },
          icon: Sun,
        })
      }
    }

    return barsArray?.filter(bar => bar.value !== 0) ?? []
  }, [
    pendingApprovalValue,
    totalDraft,
    hasNoProjects,
    serviceSelectionDrawer,
    selectedCompany,
  ])

  const activeBar = useMemo(() => {
    const activeFirstBar = AlertBarArray.find(
      bar => !barsLocalStorage?.includes(bar.id)
    )

    return activeFirstBar
  }, [AlertBarArray, barsLocalStorage])

  const handleHideForNow = (bar: Bar) => {
    if (barsLocalStorage) {
      setBarsLocalStorage([...barsLocalStorage, bar.id])
    } else {
      setBarsLocalStorage([bar.id])
    }
  }

  useEffect(() => {
    if (barsLocalStorage) {
      window.localStorage.setItem(
        'user_alerts',
        JSON.stringify(barsLocalStorage)
      )
    }
  }, [barsLocalStorage])

  useEffect(() => {
    const data = window.localStorage.getItem('user_alerts')
    if (data !== null && data.length > 0) {
      setBarsLocalStorage(JSON.parse(data))
    }
  }, [])

  if (activeBar?.key === 'create_new_project' && !hasNoProjects) return null

  const hasStack =
    AlertBarArray.length > 1 &&
    AlertBarArray[AlertBarArray.length - 1].key !== activeBar?.key

  return (
    <CSSTransition
      in={ShowAlertBars}
      timeout={400}
      classNames={{
        enter: s.alertBarIn,
        enterActive: s.alertBarEnterActive,
      }}
      unmountOnExit={true}
    >
      <div
        className={clsx(s.wrapper, {
          [s.noWrapper]: !activeBar,
        })}
      >
        {activeBar && (
          <AlertBar
            title={
              <div className={s.title}>
                <span className={s.mainTitle}>{activeBar.title}</span>
                {activeBar.key === 'approval_pending' && (
                  <StagePill stage="finished" />
                )}
              </div>
            }
            subTitle={activeBar.subTitle}
            className={clsx(s.alertBar, {
              [s.showStack]: hasStack,
            })}
            Icon={activeBar.icon}
            cancelText="Hide for now"
            ctaText={activeBar.ctaText}
            onCancelClick={() => handleHideForNow(activeBar)}
            onCtaClick={activeBar.onCtaClick}
          />
        )}
      </div>
    </CSSTransition>
  )
}

export default AlertBarWrapper
